// Adjectives — agreement, position, comparison, possessives, plus descriptive
// and color vocabulary tables. Ported from the legacy views.adjectives;
// VOCABULARY is imported directly (old shape: { es, en, cat }).
import { VOCABULARY } from "../data/index.js";
import { renderGuideSections } from "../ui/guideSections.js";

export function adjectivesView(app) {
  const adjs = VOCABULARY.filter((v) => v.cat === "adj");
  const colors = VOCABULARY.filter((v) => v.cat === "color");

  const tables = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      <div class="bg-white rounded-2xl border border-sage-100 p-5">
        <h3 class="text-lg font-bold text-sage-800 mb-3">Descriptive Adjectives</h3>
        <div class="overflow-x-auto"><table class="w-full text-sm">
          <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
            <th class="py-2 pr-3">Italian</th><th class="py-2 px-3">English</th>
          </tr></thead>
          <tbody>
            ${adjs
              .map(
                (a) => `<tr class="border-b border-sage-50 last:border-0">
              <td class="py-2 pr-3 font-medium text-sage-800">${a.es}</td>
              <td class="py-2 px-3 text-sage-700">${a.en}</td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table></div>
      </div>
      <div class="bg-white rounded-2xl border border-sage-100 p-5">
        <h3 class="text-lg font-bold text-sage-800 mb-3">Colors</h3>
        <div class="overflow-x-auto"><table class="w-full text-sm">
          <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
            <th class="py-2 pr-3">Italian</th><th class="py-2 px-3">English</th>
          </tr></thead>
          <tbody>
            ${colors
              .map(
                (c) => `<tr class="border-b border-sage-50 last:border-0">
              <td class="py-2 pr-3 font-medium text-sage-800">${c.es}</td>
              <td class="py-2 px-3 text-sage-700">${c.en}</td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table></div>
      </div>
    </div>
  `;

  renderGuideSections(
    app,
    "Adjectives",
    "Adjectives describe nouns — and in Italian, they have to match the noun in gender and number. They also usually come after the noun, not before.",
    [
      {
        title: "1. Agreement — gender & number",
        body: `
          <p class="text-sage-700 mb-3">Adjectives change their ending to match the noun they describe.</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Type</th><th class="py-2 px-3">M. sg</th><th class="py-2 px-3">F. sg</th><th class="py-2 px-3">M. pl</th><th class="py-2 px-3">F. pl</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">-o (4 forms)</td><td class="py-2 px-3">alto</td><td class="py-2 px-3">alta</td><td class="py-2 px-3">alti</td><td class="py-2 px-3">alte</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">-e (2 forms)</td><td class="py-2 px-3">grande</td><td class="py-2 px-3">grande</td><td class="py-2 px-3">grandi</td><td class="py-2 px-3">grandi</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">-ista (gender-invariable)</td><td class="py-2 px-3">ottimista</td><td class="py-2 px-3">ottimista</td><td class="py-2 px-3">ottimisti</td><td class="py-2 px-3">ottimiste</td></tr>
              <tr><td class="py-2 pr-3 font-medium">Invariable</td><td class="py-2 px-3">blu</td><td class="py-2 px-3">blu</td><td class="py-2 px-3">blu</td><td class="py-2 px-3">blu</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm">Examples: <i>il libro rosso, la casa rossa, i libri rossi, le case rosse</i>. (Colours like <i>blu, rosa, viola</i> never change.)</p>
        `,
      },
      {
        title: "2. Position — after the noun (usually)",
        body: `
          <p class="text-sage-700 mb-2">Default position is <b>after</b> the noun: <i>una casa grande</i>, <i>un libro interessante</i>.</p>
          <p class="text-sage-700 mb-2">Some adjectives change meaning depending on position:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>un <b>grande</b> uomo</i> = a great man · <i>un uomo <b>grande</b></i> = a big man</li>
            <li><i>un <b>vecchio</b> amico</i> = an old (long-time) friend · <i>un amico <b>vecchio</b></i> = an elderly friend</li>
            <li><i>un <b>povero</b> uomo</i> = a poor (pitied) man · <i>un uomo <b>povero</b></i> = a penniless man</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>A few common ones usually come before:</b> bello, buono, bravo, grande, brutto, giovane. And some clip before a noun: buono→<b>buon</b> (un buon amico), grande→<b>gran</b> (un gran libro), bello→<b>bel</b> (un bel film), santo→<b>San</b> (San Marco).</p>
        `,
      },
      {
        title: "3. Comparison",
        body: `
          <ul class="text-sage-700 text-sm space-y-2 list-disc pl-5">
            <li><b>More than:</b> <i>più + adj + di/che</i> → <i>Maria è <b>più alta di</b> Gianni.</i></li>
            <li><b>Less than:</b> <i>meno + adj + di/che</i> → <i>Il libro è <b>meno caro di</b> quello.</i></li>
            <li><b>As ... as:</b> <i>(così) + adj + come</i> / <i>(tanto) + adj + quanto</i> → <i>È <b>veloce come</b> te.</i></li>
            <li><b>Superlative:</b> <i>il/la/i/le più + adj</i> → <i>la più bella</i>.</li>
            <li><b>Irregulars:</b> buono → <b>migliore</b>, cattivo → <b>peggiore</b>, grande → <b>maggiore</b>, piccolo → <b>minore</b>.</li>
            <li><b>Absolute superlative (-issimo):</b> <i>grande → grandissimo, facile → facilissimo</i>.</li>
          </ul>
          <p class="text-sage-700 text-sm mt-2"><b>di vs che:</b> use <i>di</i> comparing two nouns (<i>più alto di Gianni</i>); use <i>che</i> comparing two qualities or before a preposition (<i>più simpatico che intelligente</i>).</p>
        `,
      },
      {
        title: "4. Possessive adjectives",
        body: `
          <div class="overflow-x-auto"><table class="w-full text-sm">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Owner</th><th class="py-2 px-3">M. sg / F. sg</th><th class="py-2 px-3">M. pl / F. pl</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">my</td><td class="py-2 px-3">il mio / la mia</td><td class="py-2 px-3">i miei / le mie</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">your (tu)</td><td class="py-2 px-3">il tuo / la tua</td><td class="py-2 px-3">i tuoi / le tue</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">his/her/its/your (Lei)</td><td class="py-2 px-3">il suo / la sua</td><td class="py-2 px-3">i suoi / le sue</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">our</td><td class="py-2 px-3">il nostro / la nostra</td><td class="py-2 px-3">i nostri / le nostre</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">your (voi)</td><td class="py-2 px-3">il vostro / la vostra</td><td class="py-2 px-3">i vostri / le vostre</td></tr>
              <tr><td class="py-2 pr-3 font-medium">their</td><td class="py-2 px-3">il loro / la loro</td><td class="py-2 px-3">i loro / le loro</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm mt-3">Italian possessives normally <b>take the definite article</b> and agree with the <b>thing owned</b>: <i>la nostra casa</i> (our house — feminine), <i>i nostri libri</i> (our books — masculine plural). Drop the article with a single, unmodified family member: <i>mia madre, mio fratello</i> (but <i>la mia sorella maggiore</i>). <i>loro</i> never changes and always keeps the article.</p>
        `,
      },
    ],
    "",
    tables
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
